import { dateFormat, figureFormat, householdLocale, localeFor, readerLocale } from '~/format/locale'

const MINOR_PER_MAJOR = 100
const BASIS_POINTS_FULL = 10_000

const COMPACT: Intl.NumberFormatOptions = { notation: 'compact', maximumFractionDigits: 1 }
const ONE_DECIMAL: Intl.NumberFormatOptions = {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
}

/**
 * Axis ticks carry no currency symbol; the chart names the currency once, which
 * keeps the tick column narrow enough to stay unrotated. Without a currency of
 * its own a tick takes the household's, so that it groups its thousands the way
 * the amount it is a scale for does.
 */
export function compactMinor(amountMinor: number): string {
  return figureFormat(householdLocale(), COMPACT).format(amountMinor / MINOR_PER_MAJOR)
}

/**
 * An amount written on the plot itself, in the axis's short form and without
 * its currency, for the same reason a tick goes without one; signed, because a
 * source can arrive owing more than it holds.
 */
export function compactSignedMinor(amountMinor: number): string {
  return figureFormat(householdLocale(), { ...COMPACT, signDisplay: 'exceptZero' }).format(
    amountMinor / MINOR_PER_MAJOR,
  )
}

export function formatSignedMinor(amountMinor: number, currency: string): string {
  return figureFormat(localeFor(currency), {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
    signDisplay: 'exceptZero',
  }).format(amountMinor / MINOR_PER_MAJOR)
}

/** Shares stay in integer basis points so a stacked column always sums to 10 000. */
export function shareBasisPoints(partMinor: number, totalMinor: number): number {
  if (totalMinor <= 0) return 0
  return Math.round((partMinor * BASIS_POINTS_FULL) / totalMinor)
}

export function formatShareBp(bp: number): string {
  return `${figureFormat(householdLocale(), ONE_DECIMAL).format(bp / 100)} %`
}

export function formatDriftBp(bp: number): string {
  const signed = figureFormat(householdLocale(), { ...ONE_DECIMAL, signDisplay: 'exceptZero' })
  return `${signed.format(bp / 100)} pp`
}

/**
 * A tick names a calendar day, not a moment, so it is read back in the zone it
 * was written in — west of Greenwich a local reading turns it into the day before.
 */
const MONTH_ONLY: Intl.DateTimeFormatOptions = {
  month: 'short',
  year: '2-digit',
  timeZone: 'UTC',
}
const DAY_MONTH: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
}
const LONG_RANGE_DAYS = 180

/**
 * Month names in the reader's own language, not the household's.
 *
 * The figures on the same chart take the household's locale so they punctuate
 * like the amounts they are a scale for. A month name has nothing to agree
 * with: it is a word, and it belongs in the language the interface is read in.
 * An axis that says "Mar" to every household in every language was the chart
 * naming the one reader it was drawn for; an axis that says "mar." to a
 * household reading English is the same mistake wearing the other hat.
 */
export function formatAxisDate(isoDate: string, spanDays: number): string {
  const at = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(at.getTime())) return isoDate
  const shape = spanDays > LONG_RANGE_DAYS ? MONTH_ONLY : DAY_MONTH
  return dateFormat(readerLocale(), shape).format(at)
}

/**
 * Which way a signed figure went, as an attribute a stylesheet can act on.
 *
 * The sign was carried by a leading "+" or "−" and nothing else, which asks the
 * reader to find one glyph in a nineteen-character kroner string. Drawing the
 * arrow from CSS rather than writing it into the string leaves what a screen
 * reader announces, and what a test reads, exactly as it was.
 */
export function direction(amountMinor: number): 'up' | 'down' | 'flat' {
  if (amountMinor > 0) return 'up'
  if (amountMinor < 0) return 'down'
  return 'flat'
}
